// Provider de color mode (dark/light/system), com persistência em localStorage.
import React, { createContext, useState, useMemo, useEffect } from 'react';
import { createTheme, ThemeProvider } from '@mui/material/styles';
import { CssBaseline } from '@mui/material';
import { getDesignTokens } from './theme';

export const ThemeContext = createContext({
    preference: 'light',
    setColorMode: () => {},
    toggleColorMode: () => {},
});

const getSystemMode = () =>
    typeof window !== 'undefined' && window.matchMedia('(prefers-color-scheme: dark)').matches
        ? 'dark'
        : 'light';

const resolveMode = (pref) => (pref === 'system' ? getSystemMode() : pref);

export const AppThemeProvider = ({ children }) => {
    const [preference, setPreference] = useState(() => {
        try {
            const saved = localStorage.getItem('themeMode');
            return saved === 'dark' || saved === 'light' || saved === 'system' ? saved : 'light';
        } catch {
            return 'light';
        }
    });

    const [mode, setMode] = useState(() => resolveMode(preference));

    useEffect(() => {
        try {
            localStorage.setItem('themeMode', preference);
        } catch {}
        document.documentElement.dataset.theme = resolveMode(preference);

        if (preference === 'system') {
            const mq = window.matchMedia('(prefers-color-scheme: dark)');
            const handler = (e) => {
                const newMode = e.matches ? 'dark' : 'light';
                setMode(newMode);
                document.documentElement.dataset.theme = newMode;
            };
            setMode(getSystemMode());
            mq.addEventListener('change', handler);
            return () => mq.removeEventListener('change', handler);
        }

        setMode(preference);
        return undefined;
    }, [preference]);

    const colorMode = useMemo(
        () => ({
            preference,
            setColorMode: (pref) => setPreference(pref),
            toggleColorMode: () => {
                const current = preference === 'system' ? getSystemMode() : preference;
                setPreference(current === 'dark' ? 'light' : 'dark');
            },
        }),
        [preference]
    );

    const theme = useMemo(() => createTheme(getDesignTokens(mode)), [mode]);

    return (
        <ThemeContext.Provider value={colorMode}>
            <ThemeProvider theme={theme}>
                <CssBaseline />
                {children}
            </ThemeProvider>
        </ThemeContext.Provider>
    );
};
